import React, { useState, useMemo, useEffect } from 'react';
import { 
  X, 
  Camera, 
  Clock, 
  Ticket, 
  Train, 
  Copy, 
  Check, 
  Bookmark, 
  ExternalLink, 
  Share2, 
  Star,
  Bus,
  Footprints,
  Navigation,
  Utensils,
  Zap,
  Heart,
  Users,
  Globe,
  Sparkles,
  Calendar,
  ShieldCheck,
  Store,
} from 'lucide-react';
import { Spot, Language, UserLocation } from '../types/spot';
import { formatDistance, getRealisticTransitInfo } from '../services/geolocation';
import { openKakaoMap, getSpotMapLinks } from '../services/mapLinks';
import { useSpotImage } from '../hooks/useSpotImage';
import { getCategoryPlaceholder, getImmediateSpotImageUrl, getSpotSocialLinks } from '../services/imageResolver';
import { InstagramIcon, TikTokIcon } from './SocialIcons';
import { findNearbyClusterSpots, findNearbyHalalAndVeggieSpots, getUpcomingEventSessions } from '../services/momentEngine';

interface SpotDetailModalProps {
  spot: Spot | null;
  allSpots: Spot[];
  onClose: () => void;
  language: Language;
  userLocation: UserLocation;
  isSaved: boolean;
  onToggleSave: (spot: Spot) => void;
  onCopyHangul: (nameKr: string) => void;
  onSelectSpot: (spot: Spot) => void;
  onOpenVeggieSnacks?: () => void;
}

export const SpotDetailModal: React.FC<SpotDetailModalProps> = ({
  spot,
  allSpots,
  onClose,
  language,
  userLocation,
  isSaved,
  onToggleSave,
  onCopyHangul,
  onSelectSpot,
  onOpenVeggieSnacks,
}) => {
  const [copiedName, setCopiedName] = useState(false);
  const { imageUrl } = useSpotImage(spot);
  const [imgOverride, setImgOverride] = useState<string | null>(null);

  useEffect(() => {
    setImgOverride(null);
    setCopiedName(false);
  }, [spot?.id]);

  useEffect(() => {
    if (!spot) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [spot, onClose]);

  const nearbyDiningSpots = useMemo(() => {
    if (!spot) return [];
    return findNearbyHalalAndVeggieSpots(spot, allSpots, 12).slice(0, 3);
  }, [spot, allSpots]);

  const upcomingSessions = useMemo(() => {
    if (!spot) return [];
    return getUpcomingEventSessions(spot);
  }, [spot]);

  if (!spot) return null;

  const currentImage = imgOverride || imageUrl;

  const title = language === 'de' ? spot.nameDe : language === 'kr' ? spot.nameKr : spot.name;
  const description = language === 'de' ? spot.descriptionDe : spot.description;
  const photoTip = language === 'de' ? spot.photoTipsDe : spot.photoTips;
  const bestTime = language === 'de' ? spot.bestTimeDe : spot.bestTime;
  const feeInfo = language === 'de' ? spot.feeInfoDe : spot.feeInfo;

  const t = {
    de: {
      shareTooltip: 'Teilen',
      closeTooltip: 'Schließen',
      copyHangulBtn: '한글 kopieren',
      copied: 'Kopiert',
      distanceLabel: 'Entfernung',
      travelTimeLabel: 'Reisezeit',
      busLabel: 'Bus & Öffis',
      walkLabel: 'Zu Fuß',
      variable: 'Variabel',
      ratingLabel: 'Bewertung',
      aboutTitle: 'Über diesen Ort',
      eventBadge: 'Event & Lichtshow',
      photoTipsTitle: 'Fototipps & Blickwinkel',
      bestTimeLabel: 'Beste Besuchszeit:',
      transitBusLabel: 'ÖPNV- & Reisezeit:',
      transitNotice: 'Reale koreanische Fahrzeiten: inkl. Haltestellen-Fußweg, Ampelphasen, Steigungen & Bus-Wartezeit.',
      kakaoLiveBtn: 'KakaoMap Live',
      subwayLabel: 'U-Bahn & Anreise:',
      feeLabel: 'Eintritt / Ticket:',
      comboTitle: 'In der Nähe (wenige Gehminuten)',
      nearbyDiningTitle: 'Halal & Veggie in der Nähe dieses Spots',
      nearbyDiningSubtitle: 'Geprüfte Halal- & vegane Empfehlungen',
      socialTitle: 'Fotos & Impressionen',
      openKakaoMap: 'In KakaoMap öffnen',
      openGoogleMaps: 'In Google Maps öffnen',
      kakaoTooltip: 'In KakaoMap öffnen (Fotos, Öffnungszeiten, Bewertungen)',
      savedTooltip: 'Gespeichert',
      saveTooltip: 'Merken',
      shareText: (sName: string) => `Entdecke ${sName} in Südkorea!`,
      naverBlog: 'Naver Blog Fotos',
      localReviews: 'Lokale Insider-Reviews',
      phraseTitle: 'Praktische koreanische Bestellphrasen',
      phrases: [
        { kr: '"고기 빼주세요"', desc: 'Gogi bbaejuseyo · Bitte ohne Fleisch' },
        { kr: '"돼지고기 안 먹어요"', desc: 'Dwaejigogi an meogeoyo · Kein Schweinefleisch' },
        { kr: '"할랄 음식인가요?"', desc: 'Halal eumsik-ingayo? · Ist das Halal?' },
        { kr: '"계란·유제품 빼주세요"', desc: 'Gyeran yujepum bbaejuseyo · Ohne Ei/Milch' },
      ],
    },
    en: {
      shareTooltip: 'Share',
      closeTooltip: 'Close',
      copyHangulBtn: 'Copy Korean Name',
      copied: 'Copied',
      distanceLabel: 'Distance',
      travelTimeLabel: 'Travel Time',
      busLabel: 'Bus & Transit',
      walkLabel: 'Walking',
      variable: 'Variable',
      ratingLabel: 'Rating',
      aboutTitle: 'About this Place',
      eventBadge: 'Event & Light Show',
      photoTipsTitle: 'Photo Tips & Angles',
      bestTimeLabel: 'Best Time to Visit:',
      transitBusLabel: 'Transit & Travel Time:',
      transitNotice: 'Realistic Korean travel times: includes bus stop walks, waiting & signal intervals.',
      kakaoLiveBtn: 'KakaoMap Live',
      subwayLabel: 'Subway & Directions:',
      feeLabel: 'Admission / Ticket:',
      comboTitle: 'Nearby (short walking distance)',
      nearbyDiningTitle: 'Halal & Veggie Dining Near This Spot',
      nearbyDiningSubtitle: 'Verified Halal & plant-based recommendations',
      socialTitle: 'Photos & Social Insights',
      openKakaoMap: 'Open in KakaoMap',
      openGoogleMaps: 'Open in Google Maps',
      kakaoTooltip: 'Open in KakaoMap (Photos, Hours, Reviews)',
      savedTooltip: 'Saved',
      saveTooltip: 'Save',
      shareText: (sName: string) => `Discover ${sName} in South Korea!`,
      naverBlog: 'Naver Blog Photos',
      localReviews: 'Local Insider Reviews',
      phraseTitle: 'Helpful Korean Ordering Phrases',
      phrases: [
        { kr: '"고기 빼주세요"', desc: 'Gogi bbaejuseyo · Please make it without meat' },
        { kr: '"돼지고기 안 먹어요"', desc: 'Dwaejigogi an meogeoyo · I do not eat pork' },
        { kr: '"할랄 음식인가요?"', desc: 'Halal eumsik-ingayo? · Is this food Halal?' },
        { kr: '"계란·유제품 빼주세요"', desc: 'Gyeran yujepum bbaejuseyo · No eggs or dairy products' },
      ],
    },
    kr: {
      shareTooltip: '공유하기',
      closeTooltip: '닫기',
      copyHangulBtn: '한글 복사',
      copied: '복사됨',
      distanceLabel: '거리',
      travelTimeLabel: '소요 시간',
      busLabel: '버스·대중교통',
      walkLabel: '도보',
      variable: '변동',
      ratingLabel: '평점',
      aboutTitle: '장소 소개',
      eventBadge: '축제 & 라이트쇼',
      photoTipsTitle: '사진 촬영 팁 & 추천 구도',
      bestTimeLabel: '추천 방문 시간:',
      transitBusLabel: '대중교통 & 이동 시간:',
      transitNotice: '정류장 도보 이동, 배차 간격 및 신호 대기 시간을 반영한 실제 소요 시간입니다.',
      kakaoLiveBtn: '카카오맵 실시간',
      subwayLabel: '지하철 및 오시는 길:',
      feeLabel: '입장료 / 티켓:',
      comboTitle: '함께 가볼 만한 곳 (도보 거리)',
      nearbyDiningTitle: '이 명소 근처 할랄 & 채식 맛집',
      nearbyDiningSubtitle: '주변 인증 할랄 및 비건/채식 추천',
      socialTitle: '소셜 미디어 둘러보기',
      openKakaoMap: '카카오맵에서 열기',
      openGoogleMaps: '구글 지도에서 열기',
      kakaoTooltip: '카카오맵에서 열기 (사진, 영업시간, 리뷰)',
      savedTooltip: '저장됨',
      saveTooltip: '저장하기',
      shareText: (sName: string) => `대한민국 명소 ${sName} 살펴보기!`,
      naverBlog: '네이버 블로그 사진',
      localReviews: '현지인 생생 리뷰',
      phraseTitle: '유용한 식당 주문 표현',
      phrases: [
        { kr: '"고기 빼주세요"', desc: 'Gogi bbaejuseyo · 고기 없이 조리 요청' },
        { kr: '"돼지고기 안 먹어요"', desc: 'Dwaejigogi an meogeoyo · 돼지고기 제외 요청' },
        { kr: '"할랄 음식인가요?"', desc: 'Halal eumsik-ingayo? · 할랄 인증/메뉴 문의' },
        { kr: '"계란·유제품 빼주세요"', desc: 'Gyeran yujepum bbaejuseyo · 계란/유제품 제외' },
      ],
    },
  }[language];

  const nearbyClusters = findNearbyClusterSpots(spot, allSpots, 0.6);
  const transitInfo = getRealisticTransitInfo(spot.calculatedDistance, language);
  const mapLinks = getSpotMapLinks(spot, userLocation);

  const handleCopyHangul = () => {
    navigator.clipboard.writeText(spot.nameKr);
    setCopiedName(true);
    onCopyHangul(spot.nameKr);
    setTimeout(() => setCopiedName(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${title} (${spot.nameKr})`,
          text: t.shareText(spot.name),
          url: `https://www.google.com/maps/search/?api=1&query=${spot.lat},${spot.lng}`,
        });
      } catch {
        // Cancelled
      }
    } else {
      handleCopyHangul();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-sheet"
        style={{ display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-image">
          <img
            src={currentImage}
            alt={title}
            referrerPolicy="no-referrer"
            onError={() => setImgOverride(getCategoryPlaceholder(spot.category))}
          />
          <div className="detail-image__overlay" />

          {/* Top Actions */}
          <div className="detail-image__actions">
            <button
              onClick={handleShare}
              className="card-action-btn card-action-btn--default"
              title={t.shareTooltip}
            >
              <Share2 style={{ width: 16, height: 16 }} />
            </button>
            <button
              onClick={onClose}
              className="card-action-btn card-action-btn--default"
              title={t.closeTooltip}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {/* Title over Image */}
          <div className="detail-image__title">
            <div className="detail-hangul-badge">
              <span className="detail-hangul-badge__text">{spot.nameKr}</span>
              <button onClick={handleCopyHangul} className="btn--inline" style={{ backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                {copiedName ? <Check style={{ width: 13, height: 13, color: '#34d399' }} /> : <Copy style={{ width: 13, height: 13 }} />}
                <span>{copiedName ? t.copied : t.copyHangulBtn}</span>
              </button>
              {spot.category === 'shopping' && (
                <span className="badge badge--shopping" style={{ marginLeft: 6 }}>
                  {language === 'kr' ? '쇼핑' : 'Shopping'}
                </span>
              )}
              {spot.category === 'activity' && (
                <span className="badge badge--activity" style={{ marginLeft: 6 }}>
                  {language === 'kr' ? '액티비티' : language === 'en' ? 'Activity' : 'Aktivität'}
                </span>
              )}
              {spot.category === 'fitness' && (
                <span className="badge badge--activity" style={{ marginLeft: 6, background: 'rgba(249, 115, 22, 0.2)', borderColor: '#f97316', color: '#f97316' }}>
                  {language === 'kr' ? '피트니스 & 헬스' : language === 'en' ? 'Fitness & Gym' : 'Fitness & Workout'}
                </span>
              )}
            </div>
            <h2 className="detail-title">{title}</h2>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Quick Metrics */}
          <div className="metric-grid">
            <div className="metric-grid__item">
              <div className="metric-grid__label">{t.busLabel}</div>
              <div className="metric-grid__value metric-grid__value--accent">
                {transitInfo
                  ? (transitInfo.busMinutes < 60
                      ? `~${transitInfo.busMinutes} Min`
                      : `~${Math.floor(transitInfo.busMinutes / 60)}h ${transitInfo.busMinutes % 60}m`)
                  : t.variable}
              </div>
            </div>
            <div className="metric-grid__item metric-grid__item--bordered">
              <div className="metric-grid__label">{t.walkLabel}</div>
              <div className="metric-grid__value metric-grid__value--blue">
                {transitInfo
                  ? (transitInfo.walkingMinutes < 60
                      ? `~${transitInfo.walkingMinutes} Min`
                      : `~${Math.floor(transitInfo.walkingMinutes / 60)}h ${transitInfo.walkingMinutes % 60}m`)
                  : t.variable}
              </div>
            </div>
            <div className="metric-grid__item metric-grid__item--bordered">
              <div className="metric-grid__label">{t.distanceLabel}</div>
              <div className="metric-grid__value metric-grid__value--dist">
                {spot.calculatedDistance !== undefined ? formatDistance(spot.calculatedDistance) : 'Korea'}
              </div>
            </div>
            <div className="metric-grid__item">
              <div className="metric-grid__label">{t.ratingLabel}</div>
              <div className="metric-grid__value metric-grid__value--amber">
                <Star style={{ width: 14, height: 14, fill: '#f59e0b', color: '#f59e0b' }} />
                <span>{spot.rating}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="detail-section__title">{t.aboutTitle}</h4>
            <p className="detail-section__text">{description}</p>
          </div>

          {/* Clean Direct Link Button to Standalone Everland Park Map, Weather & Restaurant Guide */}
          {(spot.id === 'event-gyeonggi-everland-blood-city' || spot.tags?.includes('Everland')) && (
            <a
              href="/everland-map.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '14px 16px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(2, 132, 199, 0.25) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                color: '#ffffff',
                textDecoration: 'none',
                boxShadow: '0 4px 18px rgba(16, 185, 129, 0.25)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>🗺️</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
                    {language === 'kr'
                      ? '에버랜드 전체 지도 · 실시간 날씨 · 맛집 가이드 열기'
                      : language === 'en'
                      ? 'Open Full Everland Map, Live Weather & Restaurant Guide'
                      : 'Komplette Everland Park-Map, Wetter & Restaurants öffnen'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#a7f3d0' }}>
                    {language === 'kr'
                      ? '5개 테마존 상세도, 곤돌라 노선 및 15개 레스토랑 (새 창)'
                      : language === 'en'
                      ? 'Interactive 5-zone map, cable cars & 15 restaurants (opens full page)'
                      : 'Übersichtliche Vollbild-Karte mit allen 5 Zonen, Liften & 15 Restaurants'}
                  </div>
                </div>
              </div>
              <ExternalLink style={{ width: 18, height: 18, color: '#34d399', flexShrink: 0 }} />
            </a>
          )}

          {/* Event & Festival Schedule Box */}
          {(spot.isEvent || spot.category === 'event_festival' || spot.eventDatesDe) && (
            <div className="event-detail-box">
              <div className="event-detail-box__header">
                <span className="badge badge--event">{t.eventBadge}</span>
                <span className="event-detail-box__dates">
                  {language === 'kr' ? spot.eventDatesKr || '기간 한정 축제' : language === 'en' ? spot.eventDatesEn || 'Limited Event' : spot.eventDatesDe || 'Zeitlich begrenztes Event'}
                </span>
              </div>
              {spot.eventTime && (
                <div className="event-detail-box__time">
                  <Clock style={{ width: 14, height: 14, color: 'var(--accent)' }} />
                  <span>{spot.eventTime}</span>
                </div>
              )}
            </div>
          )}

          {/* Social Event, Speed Dating & Community Box */}
          {(spot.eventType || spot.organizer || spot.targetAudienceDe) && (
            <div className="social-detail-box" style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'var(--ink)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}>
                    {spot.eventType === 'speed_dating' ? (
                      <Heart style={{ width: 16, height: 16 }} />
                    ) : (
                      <Users style={{ width: 16, height: 16 }} />
                    )}
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: 'var(--text-primary)' }}>
                      {spot.eventType === 'speed_dating'
                        ? (language === 'kr' ? '인터내셔널 스피드데이팅' : language === 'en' ? 'International Speed Dating' : 'Speed Dating & Social Mixer')
                        : spot.eventType === 'language_exchange'
                        ? (language === 'kr' ? '글로벌 언어교환 & 소셜 모임' : language === 'en' ? 'Language Exchange & Social' : 'Sprachaustausch & Social Meetup')
                        : (language === 'kr' ? '소셜 모임' : language === 'en' ? 'Social Community Meetup' : 'Social Community Meetup')}
                    </h4>
                    {spot.organizer && (
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {language === 'kr' ? `주최: ${spot.organizer}` : language === 'en' ? `Hosted by: ${spot.organizer}` : `Veranstalter: ${spot.organizer}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Age Recommendation */}
              {(spot.ageRecommendationDe || spot.ageRecommendationEn || spot.ageRecommendationKr) && (
                <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Sparkles style={{ width: 14, height: 14, color: 'var(--accent)', flexShrink: 0 }} />
                  <span>
                    <strong>{language === 'kr' ? '연령대: ' : language === 'en' ? 'Age Group: ' : 'Altersgruppe: '}</strong>
                    {language === 'kr' ? spot.ageRecommendationKr : language === 'en' ? spot.ageRecommendationEn : spot.ageRecommendationDe}
                  </span>
                </div>
              )}

              {/* Target Audience */}
              {(spot.targetAudienceDe || spot.targetAudienceEn || spot.targetAudienceKr) && (
                <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <Users style={{ width: 14, height: 14, color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    <strong>{language === 'kr' ? '대상: ' : language === 'en' ? 'Audience: ' : 'Zielgruppe: '}</strong>
                    {language === 'kr' ? spot.targetAudienceKr : language === 'en' ? spot.targetAudienceEn : spot.targetAudienceDe}
                  </span>
                </div>
              )}

              {/* Advice */}
              {(spot.socialTipsDe || spot.socialTipsEn || spot.socialTipsKr) && (
                <div style={{
                  background: 'var(--bg-surface)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  marginBottom: '12px',
                  lineHeight: 1.45,
                  border: '1px solid var(--border)',
                }}>
                  <strong style={{ color: 'var(--text-primary)' }}>💡 {language === 'kr' ? '혼자 참여 팁: ' : language === 'en' ? 'Solo Tip: ' : 'Solo-Tipp: '}</strong>
                  {language === 'kr' ? spot.socialTipsKr : language === 'en' ? spot.socialTipsEn : spot.socialTipsDe}
                </div>
              )}

              {/* Action Link Buttons: Verified Instagram, RSVP & Website */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {spot.instagramUrl && (
                  <a
                    href={spot.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #e1306c 0%, #fd1d1d 50%, #f56040 100%)',
                      color: '#ffffff',
                      fontWeight: 800,
                      fontSize: '13px',
                      textDecoration: 'none',
                      boxShadow: '0 4px 15px rgba(225, 48, 108, 0.4)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <InstagramIcon style={{ width: 16, height: 16 }} />
                    <span>
                      {language === 'kr'
                        ? '📸 공식 인스타그램 채널 & 최신 피드 열기'
                        : language === 'en'
                        ? '📸 Open Official Instagram Feed & Stories'
                        : '📸 Offizielles Instagram-Profil & Feed öffnen'}
                    </span>
                    <ExternalLink style={{ width: 14, height: 14, opacity: 0.9 }} />
                  </a>
                )}

                {spot.eventUrl && spot.eventUrl !== spot.instagramUrl && (
                  <a
                    href={spot.eventUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '12.5px',
                      textDecoration: 'none',
                      boxShadow: '0 2px 10px rgba(236, 72, 153, 0.35)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>
                      {language === 'kr'
                        ? '🎟️ 공식 참가 신청 & 예약 페이지 바로가기'
                        : language === 'en'
                        ? '🎟️ Direct Event Booking, Tickets & RSVP'
                        : '🎟️ Direkt zur Event-Anmeldung & Ticket-Buchung'}
                    </span>
                    <ExternalLink style={{ width: 14, height: 14 }} />
                  </a>
                )}

                {spot.websiteUrl && spot.websiteUrl !== spot.instagramUrl && spot.websiteUrl !== spot.eventUrl && (
                  <a
                    href={spot.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '7px',
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '12px',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Globe style={{ width: 14, height: 14, color: '#38bdf8' }} />
                    <span>
                      {language === 'kr'
                        ? '🌐 상세 일정표 & 커뮤니티 안내 페이지'
                        : language === 'en'
                        ? '🌐 Event Schedule & Community Subpage'
                        : '🌐 Terminplan, Standort & Programm-Seite öffnen'}
                    </span>
                    <ExternalLink style={{ width: 13, height: 13, opacity: 0.8 }} />
                  </a>
                )}
              </div>

              {/* Concrete Date Sessions & Direct Post Links */}
              {upcomingSessions.length > 0 && (
                <div style={{ marginTop: '14px', borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <Calendar style={{ width: 14, height: 14, color: '#f472b6' }} />
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#f9a8d4', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {language === 'kr'
                        ? `📅 확정된 일정 & 포스트 링크 (${upcomingSessions.length}회)`
                        : language === 'en'
                        ? `📅 Scheduled Dates & Direct Posts (${upcomingSessions.length} sessions)`
                        : `📅 Bestätigte Termine & Post-Links (${upcomingSessions.length} Termine)`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                    {upcomingSessions.map((session, sIdx) => (
                      <div
                        key={sIdx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: 'rgba(0, 0, 0, 0.35)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: '7px',
                          padding: '7px 10px',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '130px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8' }}>
                              {language === 'kr' ? session.dateLabelKr : language === 'en' ? session.dateLabelEn : session.dateLabelDe}
                            </span>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>• {session.time}</span>
                          </div>
                          <span style={{ fontSize: '11px', color: '#cbd5e1' }}>
                            {language === 'kr' ? session.titleEn : language === 'en' ? session.titleEn : session.titleDe}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                          {session.postUrl && (
                            <a
                              href={session.postUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                borderRadius: '5px',
                                background: 'linear-gradient(135deg, rgba(225,48,108,0.35) 0%, rgba(253,29,29,0.35) 100%)',
                                border: '1px solid rgba(225,48,108,0.6)',
                                color: '#ffffff',
                                fontSize: '11px',
                                fontWeight: 700,
                                textDecoration: 'none',
                              }}
                              title="Offiziellen Instagram-Feed & Story-Ankündigung ansehen"
                            >
                              <InstagramIcon style={{ width: 12, height: 12 }} />
                              <span>Insta</span>
                            </a>
                          )}
                          {session.rsvpUrl && (
                            <a
                              href={session.rsvpUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                padding: '4px 8px',
                                borderRadius: '5px',
                                background: 'rgba(56, 189, 248, 0.2)',
                                border: '1px solid rgba(56, 189, 248, 0.5)',
                                color: '#bae6fd',
                                fontSize: '11px',
                                fontWeight: 700,
                                textDecoration: 'none',
                              }}
                            >
                              <span>RSVP</span>
                              <ExternalLink style={{ width: 10, height: 10 }} />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Verified Official Website & Ticket Booking Portal (for Activities, Festivals & Attractions) */}
          {!(spot.eventType || spot.organizer || spot.targetAudienceDe) && (spot.websiteUrl || spot.eventUrl) && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.07) 0%, rgba(139, 92, 246, 0.05) 100%)',
              border: '1px solid rgba(0, 240, 255, 0.35)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'rgba(0, 240, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#00f0ff',
                  }}>
                    <Globe style={{ width: 18, height: 18 }} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: 800, color: '#00f0ff' }}>
                      {language === 'kr'
                        ? '공식 웹사이트 및 예약 포털'
                        : language === 'en'
                        ? 'Official Website & Booking Portal'
                        : 'Offizielle Website & Ticket-Buchung'}
                    </h4>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {language === 'kr'
                        ? '운영 안내, 예약 및 실시간 공지사항'
                        : language === 'en'
                        ? 'Operating info, reservations & announcements'
                        : 'Öffnungszeiten, Ticket-Buchung & aktuelle Infos'}
                    </span>
                  </div>
                </div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#10b981',
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  padding: '3px 8px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <ShieldCheck style={{ width: 13, height: 13 }} />
                  Verified
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(spot.eventUrl || ((spot.category === 'activity' || spot.category === 'fitness') && spot.websiteUrl)) && (
                  <a
                    href={spot.eventUrl || spot.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #00f0ff 0%, #0284c7 100%)',
                      color: '#090d16',
                      fontWeight: 800,
                      fontSize: '13px',
                      textDecoration: 'none',
                      boxShadow: '0 4px 15px rgba(0, 240, 255, 0.35)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>
                      {language === 'kr'
                        ? '🎟️ 공식 티켓 예매 / 온라인 예약 바로가기'
                        : language === 'en'
                        ? '🎟️ Direct Tickets & Advance Reservation'
                        : '🎟️ Direkt zur offiziellen Ticket-Buchung & Reservierung'}
                    </span>
                    <ExternalLink style={{ width: 14, height: 14 }} />
                  </a>
                )}

                {spot.websiteUrl && spot.websiteUrl !== spot.eventUrl && (
                  <a
                    href={spot.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '7px',
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(0, 240, 255, 0.25)',
                      color: '#f8fafc',
                      fontWeight: 600,
                      fontSize: '12px',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Globe style={{ width: 14, height: 14, color: '#00f0ff' }} />
                    <span>
                      {language === 'kr'
                        ? '🌐 공식 홈페이지 방문하기'
                        : language === 'en'
                        ? '🌐 Open Official Homepage'
                        : '🌐 Offizielle Homepage öffnen'}
                    </span>
                    <ExternalLink style={{ width: 13, height: 13, opacity: 0.8 }} />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Free Phone Charging Box */}
          {(spot.category === 'charging_spot' || spot.hasFreeCharging || spot.chargingDetailsDe) && (
            <div className="charging-detail-box" style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(6, 182, 212, 0.08) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.35)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#34d399',
                  }}>
                    <Zap style={{ width: 18, height: 18 }} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#34d399' }}>
                      {language === 'kr' ? '⚡ 무료 휴대폰 급속 충전소' : language === 'en' ? '⚡ Free Phone Fast Charging' : '⚡ Kostenlose Handy-Ladestation & Steckdosen'}
                    </h4>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {language === 'kr' ? '100% 무료 · 급속 충전 및 콘센트 이용 가능' : language === 'en' ? '100% Free · Fast charging & power outlets available' : '100% Kostenlos · Schnellladen & Steckdosen verfügbar'}
                    </span>
                  </div>
                </div>
                <span className="badge badge--charging" style={{ background: '#10b981', color: '#fff', fontWeight: 700 }}>
                  {language === 'kr' ? '무료 충전' : language === 'en' ? 'Free Charging' : 'Kostenlos'}
                </span>
              </div>

              {/* Charging Details text */}
              <p style={{ margin: '0 0 10px 0', fontSize: '13px', lineHeight: '1.5', color: '#e2e8f0' }}>
                {language === 'kr'
                  ? spot.chargingDetailsKr || '매장/시설 내 안내데스크, 고객 쉼터 또는 좌석에 무료 휴대폰 충전 서비스 및 콘센트가 마련되어 있습니다.'
                  : language === 'en'
                  ? spot.chargingDetailsEn || 'Free high-speed phone charging docks and 220V power outlets are available at the information desk, lounge, or seating areas.'
                  : spot.chargingDetailsDe || 'Kostenlose High-Speed-Ladestationen und 220V-Steckdosen stehen am Infodesk, in der Lounge oder an den Sitzbereichen bereit.'}
              </p>

              {/* Port Tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#6ee7b7', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                  🔌 USB-C Fast Charge
                </span>
                <span style={{ fontSize: '11px', background: 'rgba(6, 182, 212, 0.15)', border: '1px solid rgba(6, 182, 212, 0.3)', color: '#67e8f9', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                  🔋 Qi Wireless (Induktion)
                </span>
                <span style={{ fontSize: '11px', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#fcd34d', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                  ⚡ 220V Schuko ("콘센트")
                </span>
                <span style={{ fontSize: '11px', background: 'rgba(168, 85, 247, 0.15)', border: '1px solid rgba(168, 85, 247, 0.3)', color: '#d8b4fe', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                  📱 Apple Lightning / MagSafe
                </span>
              </div>

              {/* Korean Charging Phrase Helper */}
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#38bdf8', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>💬</span>
                  <span>{language === 'kr' ? '충전 요청 필수 한국어 표현 (클릭하여 복사)' : language === 'en' ? 'Helpful Korean phrases for charging (click to copy)' : 'Hilfreiche koreanische Lade-Phrasen (Klick zum Kopieren)'}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '6px' }}>
                  {[
                    { kr: '"휴대폰 충전 좀 할 수 있을까요?"', desc: 'Hyudaepon chungjeon jom hal su isseulkkayo? · Kann ich mein Handy kurz laden?' },
                    { kr: '"콘센트 어디에 있나요?"', desc: 'Konsenteu eodie innayo? · Wo gibt es eine Steckdose?' },
                    { kr: '"충전기 빌릴 수 있나요?"', desc: 'Chungjeongi billil su innayo? · Kann ich ein Ladekabel leihen?' },
                  ].map((phrase, idx) => (
                    <div
                      key={idx}
                      onClick={() => onCopyHangul(phrase.kr.replace(/"/g, ''))}
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        border: '1px solid rgba(255,255,255,0.05)',
                        transition: 'background 0.2s',
                      }}
                      title={language === 'kr' ? '클릭하여 복사' : 'Klicken zum Kopieren'}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#f1f5f9' }}>{phrase.kr}</span>
                      <span style={{ fontSize: '10.5px', color: '#94a3b8' }}>{phrase.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Diet & Food Info (Vegan / Vegetarian / Halal) */}
          {(spot.dietType || spot.dietDetailsDe || spot.isHalal || spot.isVegetarianOnly || spot.category === 'halal_food' || spot.category === 'vegetarian_food') && (
            <div className="diet-detail-box">
              <div className="diet-detail-box__header">
                <div className="flex items-center gap-2">
                  {spot.dietType === 'universal' ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="badge badge--universal" style={{ background: 'linear-gradient(135deg, #059669 0%, #0284c7 100%)', color: '#fff' }}>
                        {language === 'kr' ? '🌐 유니버설 (할랄 & 채식 & 고기)' : language === 'en' ? '🌐 Universal (Halal, Veggie & Meat)' : '🌐 Universell (Halal, Veggie & Fleisch)'}
                      </span>
                      <span className="badge badge--halal">🕌 Halal</span>
                      <span className="badge badge--vegetarian">{language === 'kr' ? '🥬 채식' : language === 'en' ? '🥬 Vegetarian' : '🥬 Vegetarisch'}</span>
                    </div>
                  ) : spot.dietType === 'halal' || spot.category === 'halal_food' || spot.isHalal ? (
                    <span className="badge badge--halal">🕌 Halal</span>
                  ) : spot.dietType === 'halal_friendly' ? (
                    <span className="badge badge--halal-friendly">☪️ Halal-Friendly</span>
                  ) : spot.dietType === 'vegan' || spot.isVegetarianOnly ? (
                    <span className="badge badge--vegan">{language === 'kr' ? '🌿 100% 비건' : '🌿 100% Vegan'}</span>
                  ) : (
                    <span className="badge badge--vegetarian">{language === 'kr' ? '🥬 채식' : language === 'en' ? '🥬 Vegetarian' : '🥬 Vegetarisch'}</span>
                  )}
                  <h4 className="diet-detail-box__title">
                    {language === 'kr' ? '식단 & 할랄 정보' : language === 'en' ? 'Dietary & Halal Guide' : 'Ernährungs- & Halal-Hinweise'}
                  </h4>
                </div>
              </div>

              {/* Diet text */}
              <p className="diet-detail-box__text">
                {language === 'kr'
                  ? spot.dietDetailsKr || '비건/채식 또는 할랄 맞춤 주문이 가능한 매장입니다.'
                  : language === 'en'
                  ? spot.dietDetailsEn || 'Offers specialized vegan, vegetarian, or halal-friendly dining options.'
                  : spot.dietDetailsDe || 'Bietet vegane, vegetarische oder Halal-freundliche Optionen an.'}
              </p>

              {/* Korean Order Phrase Helper */}
              <div className="diet-phrase-helper">
                <div className="diet-phrase-helper__title">
                  💬 {t.phraseTitle}
                </div>
                <div className="diet-phrase-grid">
                  {t.phrases.map((p, pIdx) => (
                    <div key={pIdx} className="diet-phrase-card">
                      <span className="diet-phrase-kr">{p.kr}</span>
                      <span className="diet-phrase-de">{p.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 24/7 Convenience Store Snacks Guide Shortcut */}
              {(spot.isVegetarianOnly ||
                spot.category === 'vegetarian_food' ||
                spot.dietType === 'vegetarian' ||
                spot.dietType === 'vegan') &&
                onOpenVeggieSnacks && (
                  <button
                    type="button"
                    onClick={onOpenVeggieSnacks}
                    className="btn btn--ghost btn--sm"
                    style={{
                      marginTop: 10,
                      width: '100%',
                      justifyContent: 'center',
                      gap: 6,
                      borderColor: 'rgba(16, 185, 129, 0.4)',
                      color: '#34d399',
                      fontSize: 12,
                      background: 'rgba(16, 185, 129, 0.08)',
                    }}
                  >
                    <Store style={{ width: 14, height: 14 }} />
                    <span>
                      {language === 'kr'
                        ? '24/7 편의점 채식 간식 가이드 (구운계란·베지밀·두부바)'
                        : language === 'en'
                        ? '24/7 Convenience Store Veggie Snacks Guide'
                        : '24/7 Convenience Store Veggie-Snacks Guide'}
                    </span>
                  </button>
                )}
            </div>
          )}

          {/* Photo Tips */}
          <div className="photo-tips-box">
            <div className="photo-tips-box__header">
              <Camera style={{ width: 16, height: 16, color: 'var(--text-secondary)' }} />
              <h4 className="photo-tips-box__title">{t.photoTipsTitle}</h4>
            </div>
            <p className="photo-tips-box__text">{photoTip}</p>
          </div>

          {/* Key Details */}
          <div className="flex flex-col gap-2">
            <div className="info-row">
              <Clock className="info-row__icon" />
              <div>
                <span className="info-row__label">{t.bestTimeLabel}</span>
                <span className="info-row__value">{bestTime}</span>
              </div>
            </div>

            {spot.calculatedDistance !== undefined && (
              <div className="transit-info-card">
                <div className="transit-info-card__header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Bus style={{ width: 16, height: 16, color: 'var(--ink)' }} />
                    <span className="transit-info-card__title">{t.transitBusLabel}</span>
                  </div>
                  <a
                    href={mapLinks.kakaoRouteWeb}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transit-info-card__link"
                    title={language === 'kr' ? '카카오맵에서 실시간 대중교통 경로 보기' : language === 'en' ? 'Open live transit route in KakaoMap' : 'Live-Fahrplan in KakaoMap öffnen'}
                  >
                    <span>{t.kakaoLiveBtn}</span>
                    <ExternalLink style={{ width: 12, height: 12 }} />
                  </a>
                </div>
                <div className="transit-info-card__body">
                  <div className="transit-info-card__detail">
                    {transitInfo?.detailedText}
                  </div>
                  <div className="transit-info-card__hint">
                    {t.transitNotice}
                  </div>
                </div>
              </div>
            )}

            <div className="info-row">
              <Train className="info-row__icon" />
              <div>
                <span className="info-row__label">{t.subwayLabel}</span>
                <span className="info-row__value">{spot.subwayInfo}</span>
              </div>
            </div>

            <div className="info-row">
              <Ticket className="info-row__icon" />
              <div>
                <span className="info-row__label">{t.feeLabel}</span>
                <span className="info-row__value">{feeInfo}</span>
              </div>
            </div>
          </div>

          {/* Micro-Cluster Combinations (< 600m Minimal Walking) */}
          {nearbyClusters.length > 0 && (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' }}>
                <Footprints style={{ width: 16, height: 16, color: 'var(--accent)' }} />
                <span>{t.comboTitle}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {nearbyClusters.slice(0, 3).map(({ spot: neighbor, distanceMeters }) => {
                  const nTitle = language === 'de' ? neighbor.nameDe : language === 'kr' ? neighbor.nameKr : neighbor.name;
                  return (
                    <button
                      key={neighbor.id}
                      onClick={() => onSelectSpot(neighbor)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: 'var(--text-primary)',
                        transition: 'all var(--duration-fast)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <img
                          src={getImmediateSpotImageUrl(neighbor)}
                          alt={nTitle}
                          onError={(e) => { e.currentTarget.src = getCategoryPlaceholder(neighbor.category); }}
                          style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-primary)' }}>
                            {nTitle}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{neighbor.nameKr}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', flexShrink: 0, paddingLeft: 8 }}>
                        {distanceMeters} m
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Halal & Veggie Dining Nearby */}
          {nearbyDiningSpots.length > 0 && (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  <Utensils style={{ width: 16, height: 16, color: 'var(--ink)' }} />
                  <span>{t.nearbyDiningTitle}</span>
                </div>
                <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{t.nearbyDiningSubtitle}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {nearbyDiningSpots.map(({ spot: neighbor, distanceKm, distanceMeters }) => {
                  const nTitle = language === 'de' ? neighbor.nameDe : language === 'kr' ? neighbor.nameKr : neighbor.name;
                  const dietBadge = neighbor.dietType === 'universal'
                    ? (language === 'kr' ? '유니버설' : language === 'en' ? 'Universal' : 'Universell')
                    : neighbor.dietType === 'halal' || neighbor.category === 'halal_food' || neighbor.isHalal
                    ? 'Halal'
                    : neighbor.dietType === 'vegan' || neighbor.isVegetarianOnly
                    ? (language === 'kr' ? '비건' : 'Vegan')
                    : (language === 'kr' ? '채식' : language === 'en' ? 'Vegetarian' : 'Veggie');

                  const shortDesc = language === 'de' ? neighbor.dietDetailsDe : language === 'kr' ? neighbor.dietDetailsKr : neighbor.dietDetailsEn;

                  return (
                    <button
                      key={neighbor.id}
                      onClick={() => onSelectSpot(neighbor)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: 'var(--text-primary)',
                        transition: 'all var(--duration-fast)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                        <img
                          src={getImmediateSpotImageUrl(neighbor)}
                          alt={nTitle}
                          onError={(e) => { e.currentTarget.src = getCategoryPlaceholder(neighbor.category); }}
                          style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                        />
                        <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: 4,
                              background: 'var(--bg-elevated)',
                              border: '1px solid var(--border)',
                              color: 'var(--text-secondary)',
                            }}>
                              {dietBadge}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-primary)' }}>
                              {nTitle}
                            </span>
                          </div>
                          {shortDesc ? (
                            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {shortDesc}
                            </div>
                          ) : (
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{neighbor.nameKr}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {distanceKm < 1 ? `${distanceMeters} m` : `${distanceKm} km`}
                        </span>
                        <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                          {language === 'kr' ? '상세보기' : 'Details'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Social Discovery Links */}
          {(() => {
            const social = getSpotSocialLinks(
              spot.nameKr,
              spot.name,
              spot.id,
              spot.instagramTag,
              spot.instagramUrl,
              spot.tiktokQuery
            );
            return (
              <div className="social-discovery-box">
                <h4 className="social-discovery-box__title">{t.socialTitle}</h4>
                <div className="social-discovery-box__grid">
                  <a
                    href={social.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-card social-card--insta"
                  >
                    <InstagramIcon size={20} />
                    <div>
                      <div className="social-card__name">Instagram Reels</div>
                      <div className="social-card__tag">{social.hashtag}</div>
                    </div>
                  </a>

                  <a
                    href={social.tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-card social-card--tiktok"
                  >
                    <TikTokIcon size={20} />
                    <div>
                      <div className="social-card__name">TikTok Trends</div>
                      <div className="social-card__tag">Korea Travel</div>
                    </div>
                  </a>

                  <a
                    href={social.naverBlogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-card social-card--naver"
                  >
                    <span className="social-card__n">N</span>
                    <div>
                      <div className="social-card__name">{t.naverBlog}</div>
                      <div className="social-card__tag">{t.localReviews}</div>
                    </div>
                  </a>
                </div>
              </div>
            );
          })()}

          {/* Action Row */}
          <div className="detail-actions" style={{ flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => openKakaoMap(spot, false, userLocation)}
                className="btn btn--kakao btn--lg btn--flex1"
                style={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px' }}
                title={t.kakaoTooltip}
              >
                <span>{t.openKakaoMap}</span>
                <ExternalLink style={{ width: 16, height: 16 }} />
              </button>

              <button
                onClick={() => onToggleSave(spot)}
                className="btn btn--icon"
                style={isSaved ? { background: 'var(--accent-subtle)', color: 'var(--accent)', borderColor: 'var(--accent)', minWidth: 48 } : { minWidth: 48 }}
                title={isSaved ? t.savedTooltip : t.saveTooltip}
              >
                <Bookmark style={{ width: 20, height: 20, fill: isSaved ? 'currentColor' : 'none' }} />
              </button>
            </div>

            <a
              href={`https://www.google.com/maps/search/?api=1&query=${spot.lat},${spot.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--md btn--full"
              style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px', borderRadius: 'var(--radius-md)' }}
            >
              <Navigation style={{ width: 16, height: 16 }} />
              <span>{t.openGoogleMaps}</span>
              <ExternalLink style={{ width: 14, height: 14 }} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
